import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View, FlatList, RefreshControl, Text, NativeSyntheticEvent, NativeScrollEvent,
} from 'react-native';
import { ActivityIndicator, FAB, Portal, Modal, Button } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch } from '../redux/hooks';
import { createClient } from '../redux/slices/clientsSlice';
import ClientCard from '../components/ClientCard';
import EmptyState from '../components/EmptyState';
import { FilterChip, IconCircleButton, PageHeader, SearchPill, TextInput } from '../components/ui';
import { CONTENT_MAX_WIDTH, RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { RootStackParamList } from '../navigation/AppNavigator';
import { ClientSummary, TypeClient } from '../types';
import { clientService } from '../services/clientService';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagedList } from '../hooks/usePagedList';

type Nav = StackNavigationProp<RootStackParamList, 'Main'>;

const TYPE_FILTERS = ['TOUS', 'PARTICULIER', 'AGENCE'] as const;

export default function ListeClients() {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<Nav>();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, isWide, gutter, width } = useLayout();
  const numColumns = isWide ? 2 : 1;

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [vehiculeFilter, setVehiculeFilter] = useState('');
  const [nom, setNom] = useState('');
  const [type, setType] = useState<TypeClient>('PARTICULIER');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [ice, setIce] = useState('');
  const [nomResponsable, setNomResponsable] = useState('');
  const [iceError, setIceError] = useState('');
  const [nomResponsableError, setNomResponsableError] = useState('');
  const [telephoneError, setTelephoneError] = useState('');
  const [typeFilter, setTypeFilter] = useState<'TOUS' | 'PARTICULIER' | 'AGENCE'>('TOUS');
  const [saving, setSaving] = useState(false);
  const listRef = useRef<FlatList<ClientSummary>>(null);
  const canLoadMoreRef = useRef(false);

  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedVehicule = useDebouncedValue(vehiculeFilter, 300);
  const searchQuery = search.trim() ? debouncedSearch.trim() : '';
  const vehiculeQuery = vehiculeFilter.trim() ? debouncedVehicule.trim() : '';
  const queryKey = `${typeFilter}|${searchQuery}|${vehiculeQuery}`;

  const fetchPage = useCallback(
    (page: number, size: number) => clientService.getPage({
      page,
      size,
      q: searchQuery,
      type: typeFilter,
      vehicule: vehiculeQuery,
    }),
    [searchQuery, typeFilter, vehiculeQuery],
  );
  const {
    items: clients,
    total,
    loading,
    loadingMore,
    hasMore,
    refresh,
    loadMore,
    epoch,
    error,
  } = usePagedList(fetchPage, queryKey);

  useEffect(() => {
    canLoadMoreRef.current = false;
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [epoch]);

  const openClient = useCallback((clientId: number) => {
    navigation.navigate('ProfilClient', { clientId });
  }, [navigation]);

  const renderClient = useCallback(({ item }: { item: ClientSummary }) => (
    numColumns > 1 ? (
      <View style={styles.gridCell}>
        <ClientCard client={item} onPress={openClient} style={styles.gridCard} />
      </View>
    ) : (
      <ClientCard client={item} onPress={openClient} />
    )
  ), [openClient, numColumns, styles]);

  const handleEndReached = useCallback(() => {
    if (!canLoadMoreRef.current) return;
    loadMore();
  }, [loadMore]);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (event.nativeEvent.contentOffset.y > 24) {
      canLoadMoreRef.current = true;
    }
  }, []);

  const handleCreate = async () => {
    if (!nom.trim()) return;
    let valid = true;
    if (type === 'AGENCE') {
      if (!/^\d{15}$/.test(ice)) { setIceError('ICE invalide (15 chiffres requis)'); valid = false; } else { setIceError(''); }
      if (!nomResponsable.trim()) { setNomResponsableError('Nom du responsable requis'); valid = false; } else { setNomResponsableError(''); }
    }
    if (type === 'PARTICULIER') {
      if (!telephone.trim()) { setTelephoneError('Le téléphone est obligatoire'); valid = false; } else { setTelephoneError(''); }
    }
    if (!valid) return;
    setSaving(true);
    await dispatch(createClient({
      nomRaisonSociale: nom.trim(),
      type,
      telephone,
      email,
      ...(type === 'AGENCE' ? { ice, nomResponsable } : {}),
    }));
    setSaving(false);
    setShowModal(false);
    setNom(''); setTelephone(''); setEmail(''); setIce(''); setNomResponsable('');
    setIceError(''); setNomResponsableError(''); setTelephoneError('');
    refresh();
  };

  const toggleVehiculeFilter = () => {
    setShowFilter(!showFilter);
    if (showFilter) setVehiculeFilter('');
  };

  const columnStyle = { paddingHorizontal: gutter };

  return (
    <View style={styles.container}>
      <View style={[styles.column, columnStyle, { paddingTop: isDesktop ? 32 : 16 }]}>
        <PageHeader
          title="Clients"
          subtitle={`${total} client${total !== 1 ? 's' : ''}`}
          actions={isDesktop ? (
            <Button mode="contained" icon="plus" onPress={() => setShowModal(true)}>
              Nouveau client
            </Button>
          ) : undefined}
        />

        <View style={[styles.toolbar, isDesktop && styles.toolbarDesktop]}>
          <SearchPill
            placeholder="Rechercher un client..."
            value={search}
            onChangeText={setSearch}
            loading={loading && search.trim().length > 0}
            style={isDesktop ? styles.searchDesktop : undefined}
            trailing={(
              <IconCircleButton
                icon={showFilter ? 'filter-remove' : 'filter-variant'}
                variant="ghost"
                size={36}
                color={showFilter ? colors.primary : colors.textPrimary}
                accessibilityLabel={showFilter ? 'Retirer le filtre véhicule' : 'Filtrer par IMEI, N° Tel SIM ou plaque'}
                onPress={toggleVehiculeFilter}
              />
            )}
          />

          <View style={styles.chipsRow}>
            {TYPE_FILTERS.map((t) => (
              <FilterChip
                key={t}
                label={t === 'TOUS' ? 'Tous' : t === 'PARTICULIER' ? 'Particuliers' : 'Sociétés'}
                selected={typeFilter === t}
                onPress={() => setTypeFilter(t)}
              />
            ))}
          </View>
        </View>

        {showFilter && (
          <TextInput
            dense
            placeholder="Filtrer par IMEI, N° Tel SIM ou plaque..."
            value={vehiculeFilter}
            onChangeText={setVehiculeFilter}
            outlineStyle={styles.filterOutline}
            style={[styles.filterInput, isDesktop && styles.filterInputDesktop]}
            left={<TextInput.Icon icon={loading && vehiculeFilter.trim().length > 0 ? 'loading' : 'car-info'} color={colors.textSecondary} />}
            right={vehiculeFilter ? <TextInput.Icon icon="close" color={colors.textPrimary} onPress={() => setVehiculeFilter('')} /> : null}
          />
        )}
      </View>

      {loading && clients.length === 0 ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : (
        <FlatList
          key={`clients-${numColumns}`}
          ref={listRef}
          style={styles.listFlex}
          data={clients}
          numColumns={numColumns}
          columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderClient}
          keyboardShouldPersistTaps="handled"
          initialNumToRender={8}
          maxToRenderPerBatch={8}
          windowSize={7}
          updateCellsBatchingPeriod={50}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          ListEmptyComponent={
            <EmptyState
              icon={error ? 'alert-circle-outline' : vehiculeQuery ? 'car-off' : 'account-search-outline'}
              message={
                error ?? (vehiculeQuery
                  ? 'Aucun client avec cet IMEI, N° SIM ou plaque'
                  : searchQuery ? 'Aucun client trouvé' : 'Aucun client enregistré')
              }
            />
          }
          ListFooterComponent={loadingMore ? (
            <ActivityIndicator color={colors.primary} style={styles.footer} />
          ) : hasMore ? (
            <Button
              mode="outlined"
              onPress={loadMore}
              textColor={colors.textPrimary}
              style={[styles.footer, styles.moreBtn]}
            >
              Afficher plus
            </Button>
          ) : null}
          refreshControl={(
            <RefreshControl
              refreshing={loading && clients.length > 0}
              onRefresh={refresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
              progressBackgroundColor={colors.surface}
            />
          )}
          contentContainerStyle={[
            styles.column,
            styles.list,
            columnStyle,
            { paddingBottom: isDesktop ? 48 : 96 },
          ]}
        />
      )}

      {!isDesktop && (
        <FAB
          icon="plus"
          style={styles.fab}
          color={colors.onPrimary}
          accessibilityLabel="Nouveau client"
          onPress={() => setShowModal(true)}
        />
      )}

      <Portal>
        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          contentContainerStyle={[styles.modal, { width: Math.min(560, width - 32) }]}
        >
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Nouveau Client</Text>
            <IconCircleButton
              icon="close"
              variant="ghost"
              size={36}
              accessibilityLabel="Fermer"
              onPress={() => setShowModal(false)}
            />
          </View>

          <TextInput
            label="Nom / Raison Sociale *"
            value={nom}
            onChangeText={setNom}
            style={styles.input}
          />

          <Text style={styles.fieldLabel}>Type</Text>
          <View style={styles.typeRow}>
            {(['PARTICULIER', 'AGENCE'] as const).map((t) => (
              <FilterChip
                key={t}
                label={t === 'PARTICULIER' ? 'Particulier' : 'Agence'}
                icon={t === 'PARTICULIER' ? 'account-outline' : 'domain'}
                selected={type === t}
                onPress={() => setType(t)}
                style={styles.typeChip}
              />
            ))}
          </View>

          <TextInput
            label="Téléphone *"
            value={telephone}
            onChangeText={(t) => { setTelephone(t); setTelephoneError(''); }}
            keyboardType="phone-pad"
            style={styles.input}
            error={!!telephoneError}
          />
          {!!telephoneError && <Text style={styles.fieldError}>{telephoneError}</Text>}
          <TextInput
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            style={styles.input}
          />

          {type === 'AGENCE' && (
            <>
              <TextInput
                label="ICE *"
                value={ice}
                onChangeText={(t) => { setIce(t.replace(/\D/g, '').slice(0, 15)); setIceError(''); }}
                keyboardType="numeric"
                maxLength={15}
                placeholder="15 chiffres"
                style={styles.input}
                error={!!iceError}
              />
              {!!iceError && <Text style={styles.fieldError}>{iceError}</Text>}
              <TextInput
                label="Nom du responsable *"
                value={nomResponsable}
                onChangeText={(t) => { setNomResponsable(t); setNomResponsableError(''); }}
                style={styles.input}
                error={!!nomResponsableError}
              />
              {!!nomResponsableError && <Text style={styles.fieldError}>{nomResponsableError}</Text>}
            </>
          )}

          <View style={styles.modalActions}>
            <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button
              mode="contained"
              onPress={handleCreate}
              loading={saving}
              disabled={saving || !nom.trim()}
            >
              Enregistrer
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  container: { flex: 1, backgroundColor: c.background },
  column: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  footer: { marginVertical: 16 },
  moreBtn: { alignSelf: 'center', borderColor: c.border, borderRadius: RADIUS.pill },
  toolbar: { gap: 12, marginBottom: 12 },
  toolbarDesktop: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 16 },
  searchDesktop: { flex: 1, minWidth: 320, maxWidth: 560 },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterInput: { marginBottom: 12 },
  filterInputDesktop: { maxWidth: 560 },
  filterOutline: { borderRadius: RADIUS.pill },
  listFlex: { flex: 1 },
  list: { flexGrow: 1, paddingTop: 8 },
  columnWrapper: { marginHorizontal: -6 },
  gridCell: { width: '50%', paddingHorizontal: 6 },
  gridCard: { flex: 1 },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: c.primary,
  },
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    maxWidth: 560,
    alignSelf: 'center',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, flexShrink: 1 },
  input: { marginBottom: 12 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textSecondary, marginBottom: 8 },
  typeRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  typeChip: { flex: 1, justifyContent: 'center' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12 },
  fieldError: { color: c.danger, fontSize: 12, marginTop: -6, marginBottom: 8, marginLeft: 4 },
}));
